import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import {
  type ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import { z } from 'zod'
import { ExportListButton } from '~/components/Filters/ExportListButton'
import { FilterBar } from '~/components/Filters/FilterBar/FilterBar'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import {
  resolveFilters,
  toFiltersState
} from '~/components/Filters/FilterBar/utils'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { Tab, Tabs } from '~/components/Tabs/Tabs'
import { buildActionsSearchParams } from '~/domains/actions/queries/actions'
import { TaskSections } from '~/domains/tasks/components/TaskSections/TaskSections'
import { useTasksOnboarding } from '~/domains/tasks/hooks/useTasksOnboarding'
import { buildTaskListParams } from '~/domains/tasks/queries/taskListParams'
import { toTaskActionsParams, useTasks } from '~/domains/tasks/queries/tasks'
import { contentWrapper } from '~/pages/styles/Dashboard.css'
import { quickControlsBar } from '~/pages/styles/Orders.css'
import { AuthContext } from '~/utils/contextes'
import useDocumentTitle from '~/utils/documentTitle'
import { Header } from './-components/Header'
import { useTaskFilterDefinitions } from './-components/taskFilterDefinitions'

const searchSchema = z.object({
  view: z.enum(['todo', 'done']).optional(),
  user: z.string().optional(),
  agency: z.string().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
  linkedTo: z.string().optional(),
  applicants: z.string().optional(),
  contacts: z.string().optional(),
  createdBy: z.string().optional(),
  completedBy: z.string().optional(),
  dueAfter: z.string().optional(),
  dueBefore: z.string().optional()
})

const startOfMinute = (date: Date) => {
  const rounded = new Date(date)
  rounded.setSeconds(0, 0)
  return rounded
}

const Tasks = (): ReactElement => {
  const navigate = useNavigate({ from: '/tasks/' })
  const { view = 'todo', ...panelFilters } = useSearch({
    from: '/_dashboard/tasks/'
  })
  const currentUser = useContext(AuthContext)
  const filterDefinitions = useTaskFilterDefinitions(view)

  useDocumentTitle('Tâches')

  const defaultFilters = useMemo(
    (): FiltersState => (currentUser ? { user: String(currentUser.id) } : {}),
    [currentUser]
  )
  const [defaultsResolved, setDefaultsResolved] = useState(false)
  // Frozen for the page lifetime: a ticking clock would refetch the overdue list every minute
  const [now] = useState(() => startOfMinute(new Date()))

  const resolvedFilters = resolveFilters(panelFilters, filterDefinitions)
  const filtersKey = JSON.stringify(resolvedFilters)
  // biome-ignore lint/correctness/useExhaustiveDependencies: resolvedFilters is rebuilt on every render, its serialized form is the stable dependency
  const params = useMemo(
    () => ({
      ...buildTaskListParams(
        view === 'done'
          ? { ...resolvedFilters, status: 'completed' }
          : resolvedFilters,
        now
      ),
      // Wait for the saved default search or the default filters before the first fetch
      enabled: defaultsResolved
    }),
    [filtersKey, now, defaultsResolved, view]
  )

  const {
    data,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useTasks(params)

  const { startTour } = useTasksOnboarding(defaultsResolved && !isPending)

  const tasks = useMemo(
    () => data?.pages.flatMap((page) => page['hydra:member']) ?? [],
    [data]
  )

  const viewSearch = view === 'done' ? { view } : {}
  const setFilters = useCallback(
    (filters: FiltersState, areDefault = false) => {
      const clean = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== '')
      )
      void navigate({
        search: { ...clean, ...viewSearch },
        replace: areDefault
      })
    },
    [navigate, viewSearch]
  )

  const resetFilters = () => {
    void navigate({ search: { ...defaultFilters, ...viewSearch } })
  }

  // Each tab drops the filters of the other one (status on open tasks, completer on treated ones)
  const setView = (nextView: string) => {
    const {
      status: _status,
      completedBy: _completedBy,
      ...filters
    } = panelFilters
    void navigate({
      search: nextView === 'done' ? { ...filters, view: 'done' } : filters
    })
  }

  return (
    <div className={contentWrapper}>
      <Header onStartTour={startTour} />
      <Paper>
        <div data-onboarding="tasks-tabs">
          <Tabs value={view} onChange={setView}>
            <Tab name="todo">À faire</Tab>
            <Tab name="done">Terminées</Tab>
          </Tabs>
        </div>
        <div className={quickControlsBar} data-onboarding="tasks-filters">
          <FilterBar
            definitions={filterDefinitions}
            filters={toFiltersState(panelFilters)}
            onChange={setFilters}
            savedSearchesKey="search.saved_tasks"
            defaultFilters={defaultFilters}
            onDefaultsResolved={() => setDefaultsResolved(true)}
            analyticsKey="tasks"
          />
          {defaultsResolved && (
            <ExportListButton
              resourceKey="tasks"
              filename="taches.xlsx"
              params={buildActionsSearchParams(toTaskActionsParams(params))}
            />
          )}
        </div>
        <TaskSections
          tasks={tasks}
          isLoading={!defaultsResolved || isPending}
          isError={isError}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          now={now}
          treated={view === 'done'}
          emptyMessage={
            view === 'done' ? 'Aucune tâche terminée' : 'Aucune tâche à faire'
          }
          onResetFilters={resetFilters}
        />
      </Paper>
    </div>
  )
}

export const Route = createFileRoute('/_dashboard/tasks/')({
  validateSearch: searchSchema,
  component: Tasks
})
